import React from 'react';
import { Shield, CheckCircle, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const badge = 'bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08]';
const cardBg = 'bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06]';

const CERTIFICATIONS = ['PCI DSS', 'GDPR', 'SOC 2'];

const EN_DEFAULTS = {
  heading: 'Your privacy is always protected',
  description: 'Enjoy total peace of mind with discreet billing, secure payments, and privacy controls built for you.',
  features: ['Discreet, anonymous billing', 'End-to-end encrypted messaging', 'Secure payment processing', 'Two-factor authentication', 'Full control over your data'],
  certifications: CERTIFICATIONS,
};

const SecurityBlock = () => {
  const { t } = useTranslation('auth');
  const d = {
    badge: t('landing.security.badge', 'Privacy First'),
    heading: t('landing.security.heading', EN_DEFAULTS.heading),
    description: t('landing.security.description', EN_DEFAULTS.description),
    features: t('landing.security.features', { returnObjects: true, defaultValue: EN_DEFAULTS.features }),
    certifications: EN_DEFAULTS.certifications,
    encryptionStrength: t('landing.security.encryptionStrength', '256-bit'),
    sslEncryption: t('landing.security.sslEncryption', 'SSL Encryption'),
  };

  return (
    <section className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <div className={`inline-flex items-center gap-2 px-3.5 py-1.5 ${badge} rounded-full mb-7`}>
              <Shield className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.15em]">{d.badge}</span>
            </div>
            <h2 className="cl-serif text-3xl sm:text-4xl lg:text-[3rem] font-black tracking-[-0.02em] mb-5 leading-[1.1]">{d.heading}</h2>
            <p className="text-gray-500 dark:text-gray-400 leading-relaxed mb-10 text-base font-light">{d.description}</p>
            <div className="space-y-4">
              {d.features.map((feature, i) => (
                <div key={i} className="flex items-center gap-4 group">
                  <div className="w-7 h-7 rounded-xl bg-emerald-100 dark:bg-emerald-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 dark:group-hover:bg-emerald-500/20 transition-colors">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="text-[15px] text-gray-700 dark:text-gray-300 font-light">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={`${cardBg} rounded-[2rem] p-10 sm:p-14 text-center relative overflow-hidden shadow-sm dark:shadow-none`}>
            <div className="absolute -top-20 -right-20 w-48 h-48 bg-emerald-100 dark:bg-emerald-500/[0.05] rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-teal-100 dark:bg-teal-500/[0.05] rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="w-20 h-20 mx-auto mb-7 rounded-[1.25rem] bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 flex items-center justify-center" style={{ animation: 'cl-glow 3s ease-in-out infinite' }}>
                <Lock className="w-9 h-9 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="cl-serif text-5xl sm:text-6xl font-black text-emerald-600 dark:text-emerald-400 mb-2 tracking-tight">{d.encryptionStrength}</div>
              <div className="text-sm text-gray-500 font-medium mb-8">{d.sslEncryption}</div>
              <div className="grid grid-cols-3 gap-3">
                {d.certifications.map((cert, i) => (
                  <div key={i} className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-xl py-4 text-center hover:border-emerald-400 dark:hover:border-emerald-500/30 transition-colors">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecurityBlock;
